import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import tokenStore from '../services/tokenStore';
import { clearLocalSession } from '../utils/clearSession';

const AuthContext = createContext();

// Inactivity timeout in milliseconds (5 minutes = 300000ms)
const INACTIVITY_TIMEOUT = 5 * 60 * 1000;

// Users can opt out of auto-logout from Settings → Security (e.g. so live
// GPS Fleet tracking doesn't kick them off the map). Defaults to enabled.
const isAutoLogoutEnabled = () => {
  try {
    const raw = localStorage.getItem('maidly_settings');
    if (!raw) return true;
    const parsed = JSON.parse(raw);
    return parsed?.security?.autoLogout !== false;
  } catch {
    return true;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoLogoutEnabled, setAutoLogoutEnabled] = useState(isAutoLogoutEnabled);
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Keep the auto-logout flag in sync with Settings. The Settings page
  // broadcasts `maidly:settings-updated` on save; we also listen to the
  // native `storage` event so other tabs pick up the change too.
  useEffect(() => {
    const sync = () => setAutoLogoutEnabled(isAutoLogoutEnabled());
    window.addEventListener('maidly:settings-updated', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('maidly:settings-updated', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  // Logout function
  const logout = useCallback((showMessage = true, reason = '') => {
    setUser(null);
    tokenStore.clearAccessToken();
    // Full teardown: session + workspace pointer + cached business data, and
    // disconnect realtime so the next account on this browser starts clean.
    clearLocalSession();
    // Notify listeners (LicenseContext re-evaluates whether the license
    // gate should run — non-owners bypass it entirely).
    window.dispatchEvent(new CustomEvent('maidly:auth-changed'));
    // Clear server-side cookies too
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {});
    } catch { /* ignore */ }
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    if (showMessage) {
      if (reason === 'inactivity') {
        toast.info('You have been logged out due to inactivity');
      } else {
        toast.success('Logged out successfully');
      }
    }
  }, []);

  // Reset the inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (!user) return;

    lastActivityRef.current = Date.now();
    localStorage.setItem('maidly_last_activity', lastActivityRef.current.toString());

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Respect the user's Settings → Security preference. When disabled we
    // still keep `maidly_last_activity` fresh so re-enabling doesn't stale-out.
    if (!autoLogoutEnabled) return;

    timeoutRef.current = setTimeout(() => {
      logout(true, 'inactivity');
    }, INACTIVITY_TIMEOUT);
  }, [user, logout, autoLogoutEnabled]);

  // Check if session expired on load
  useEffect(() => {
    const storedUser = localStorage.getItem('maidly_current_user');
    const lastActivity = localStorage.getItem('maidly_last_activity');
    
    if (storedUser) {
      // Check if session expired while away (skip when the user opted out
      // of auto-logout — their session should survive long idle periods).
      if (lastActivity && isAutoLogoutEnabled()) {
        const timeSinceActivity = Date.now() - parseInt(lastActivity);
        if (timeSinceActivity > INACTIVITY_TIMEOUT) {
          // Session expired
          localStorage.removeItem('maidly_current_user');
          localStorage.removeItem('maidly_last_activity');
          setLoading(false);
          return;
        }
      }
      
      setUser(JSON.parse(storedUser));
      lastActivityRef.current = Date.now();
      localStorage.setItem('maidly_last_activity', lastActivityRef.current.toString());

      // Bootstrap access token via the httpOnly refresh cookie. Access
      // token only lives in tokenStore (memory) — never in localStorage.
      (async () => {
        try {
          const r = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`,
            { method: 'POST', credentials: 'include',
              headers: { 'Content-Type': 'application/json' }, body: '{}' }
          );
          if (r.ok) {
            const data = await r.json();
            if (data?.access_token) {
              tokenStore.setAccessToken(data.access_token);
            }
          }
        } catch { /* offline — user stays cached, will refresh on next online */ }
      })();
    }
    setLoading(false);
  }, []);

  // Set up activity listeners when user is logged in
  useEffect(() => {
    if (!user) return;

    // Activity events to track
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ];

    // Throttle the reset function to avoid excessive calls
    let throttleTimeout = null;
    const throttledReset = () => {
      if (throttleTimeout) return;
      throttleTimeout = setTimeout(() => {
        throttleTimeout = null;
        resetInactivityTimer();
      }, 1000); // Only reset once per second max
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledReset, { passive: true });
    });

    // Start the initial timer
    resetInactivityTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, throttledReset);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [user, resetInactivityTimer]);

  // Handle visibility change (tab switching)
  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // If auto-logout is off, just refresh the activity stamp and bail.
        if (!autoLogoutEnabled) {
          lastActivityRef.current = Date.now();
          localStorage.setItem('maidly_last_activity', lastActivityRef.current.toString());
          return;
        }
        // Check if session expired while tab was hidden
        const lastActivity = localStorage.getItem('maidly_last_activity');
        if (lastActivity) {
          const timeSinceActivity = Date.now() - parseInt(lastActivity);
          if (timeSinceActivity > INACTIVITY_TIMEOUT) {
            logout(true, 'inactivity');
          } else {
            resetInactivityTimer();
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, logout, resetInactivityTimer, autoLogoutEnabled]);

  const login = async (email, password) => {
    // Default accounts for admin, manager, and employee (local fallback)
    const defaultAccounts = [
      { id: 'admin-001', name: 'Admin', email: 'admin', password: 'admin', role: 'admin', status: 'active' },
      { id: 'admin-002', name: 'Administrator', email: 'admin@maidly.com', password: 'admin123', role: 'admin', status: 'active' },
      { id: 'manager-001', name: 'Manager', email: 'manager', password: 'manager', role: 'manager', status: 'active' },
      { id: 'manager-002', name: 'Manager', email: 'manager@maidly.com', password: 'manager123', role: 'manager', status: 'active' },
      { id: 'employee-001', name: 'Employee', email: 'employee', password: 'employee', role: 'employee', status: 'active' },
      { id: 'employee-002', name: 'Employee', email: 'employee@maidly.com', password: 'employee123', role: 'employee', status: 'active' },
    ];

    const employees = JSON.parse(localStorage.getItem('maidly_employees') || '[]');
    const allAccounts = [...defaultAccounts, ...employees];
    
    // First, try local accounts
    const account = allAccounts.find(e => 
      (e.email === email || e.email.toLowerCase() === email.toLowerCase()) && 
      e.password === password && 
      e.status === 'active'
    );

    if (account) {
      const userData = {
        id: account.id,
        name: account.name,
        email: account.email,
        role: account.role
      };
      setUser(userData);
      localStorage.setItem('maidly_current_user', JSON.stringify(userData));
      lastActivityRef.current = Date.now();
      localStorage.setItem('maidly_last_activity', lastActivityRef.current.toString());
      window.dispatchEvent(new CustomEvent('maidly:auth-changed'));
      toast.success('Login successful!');
      return true;
    }
    
    // If local login fails, try backend API (supports username login)
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (data.requires_2fa) {
        // Store pending 2FA info and redirect
        localStorage.setItem('maidly_pending_2fa', JSON.stringify({ email: data.email }));
        toast.info('2FA verification required. Check your email.');
        window.location.href = '/secure-login?2fa=true';
        return true; // Return true to prevent "Invalid credentials" toast
      }
      
      if (response.ok && data.access_token && data.user) {
        const userData = {
          id: data.user.id,
          name: data.user.name,
          email: data.user.email,
          role: data.user.role || 'user',
          org_role: data.user.org_role || null,
          organization_id: data.user.organization_id || null,
          organization_name: data.user.organization_name || null
        };
        setUser(userData);
        tokenStore.setAccessToken(data.access_token);
        localStorage.setItem('maidly_current_user', JSON.stringify(userData));
        // Refresh token lives in an httpOnly cookie (server-set);
        // access token lives in tokenStore (memory). Nothing token-related
        // is mirrored to localStorage.
        lastActivityRef.current = Date.now();
        localStorage.setItem('maidly_last_activity', lastActivityRef.current.toString());
        window.dispatchEvent(new CustomEvent('maidly:auth-changed'));
        toast.success('Login successful!');
        return true;
      }
    } catch (error) {
      console.error('Backend login error:', error);
    }
    
    toast.error('Invalid credentials');
    return false;
  };

  return (
    <AuthContext.Provider value={{ user, login, logout: () => logout(true), loading }}>
      {children}
    </AuthContext.Provider>
  );
};
